import { Percent } from '@uniswap/sdk-core';
import { Actions } from '@uniswap/v4-sdk';
import { PositionInfo } from '@/utils/types';
import { AssetConfig } from './onchain/assets';
import { Pool, PoolKey, PoolState } from './onchain/pools';

export type OnchainAccountAddress = `0x${string}`;

export type WagmiFetchBalanceResult = {
  decimals: number;
  formatted: string;
  symbol: string;
  value: bigint;
};

export type Swap = {
  inputAmount: number;
  inputToken: AssetConfig;
  outputAmount: number;
  outputToken: AssetConfig;
  inputAmountFiat: number;
  outputAmountFiat: number;
  maxSlippagePercent: number;
};

export type Amount = {
  formatted: Maybe<string>;
  bigint: bigint;
  numeric: number;
  notional: number;
  input: Maybe<string>;
};

export type SwapParams = {
  recipient: `0x${string}` | undefined;

  currency0: `0x${string}`;
  currency1: `0x${string}`;
  fee: number;
  tickSpacing: number;
  hooks: `0x${string}`;
  zeroForOne: boolean;
  // exact amount specified by user, either input or output
  amountSpecified: bigint;
  sqrtPriceLimitX96: bigint;
  action: Actions;
  withdrawTokens: boolean;
  settleUsingTransfer: boolean;
  currencyAlreadySent: boolean;
  hookData: `0x${string}`;

  // assets
  inputAsset: AssetConfig;
  outputAsset: AssetConfig;

  // amounts
  inputAmount: bigint;
  outputAmount: bigint;

  // additional params
  inputFiatPrice: number;
  outputFiatPrice: number;

  // swap amount in human readable format
  inputAmountInHumanReadableUnit: number;
  outputAmountInHumanReadableUnit: number;

  // pool state
  pool: Pool;
  poolState: PoolState;

  // max slippage (in bps)
  maxSlippagePercent: bigint;
  autoMaxSlippage: boolean;

  // universal router execution deadline
  deadline: number;
};

export type SwapSettings = {
  autoMaxSlippage: boolean;
  maxSlippagePercent: string;
  executionDeadlineDeltaSeconds?: number;
};

export type QuoteExactSingleParams = {
  poolKey: PoolKey;
  zeroForOne: boolean;
  recipient: `0x${string}`;
  exactAmount: bigint;
  sqrtPriceLimitX96: bigint;
  hookData: `0x${string}`;
};

export type Erc20ApproveParams = {
  spender: `0x${string}`;
  amount: bigint;
  tokenAddress: `0x${string}`;
};

export type Erc20AllowanceParams = {
  owner: `0x${string}`;
  spender: `0x${string}`;
  tokenAddress: `0x${string}`;
};

export type Permit2ApproveParams = {
  spender: `0x${string}`;
  amount: bigint;
  tokenAddress: `0x${string}`;
};

export enum CurrencyField {
  CURRENCY_0 = 'CURRENCY_0',
  CURRENCY_1 = 'CURRENCY_1',
}

export type AddLiquidityParams = {
  pool: Pool | undefined;
  poolState: PoolState | undefined;
  priceRange: PriceRange;
  asset0: AssetConfig | undefined;
  asset1: AssetConfig | undefined;
  tickLower: number;
  tickUpper: number;
  inRange: boolean;
  poolCurrentPrice: number;
  asset0DepositAmount: bigint;
  asset1DepositAmount: bigint;
  asset0DepositAmountHumanReadable: string;
  asset1DepositAmountHumanReadable: string;
  currencyField: CurrencyField;
  liquidity: bigint;
  hookData: `0x${string}`;
  liquidityAction: LiquidityAction;
  positionId: bigint | undefined;
};

export type RemoveLiquidityParams = {
  asset0: AssetConfig | undefined;
  asset1: AssetConfig | undefined;
  asset0Amount: bigint;
  asset1Amount: bigint;
  asset0AmountToRemove: bigint;
  asset1AmountToRemove: bigint;
  pool: Pool | undefined;
  poolState: PoolState | undefined;
  positionInfo: PositionInfo | undefined;
  removeLiquidityPercentage: number;
  slippageTolerance: Percent | undefined;
  hookData: `0x${string}` | undefined;
  burnToken?: boolean;
};

export type CollectFeesParams = {
  recipient: `0x${string}` | undefined;
  poolState: PoolState | undefined;
  positionInfo: PositionInfo | undefined;
};

export type PriceRange = {
  priceLower: number;
  priceUpper: number;
  isFullRange: boolean;
  priceUnitInAsset0: boolean;
};

export type Hash = `0x${string}`;

export type EASAttestation = {
  attester: Hash; // the attester who created the attestation.
  data: Hash; // the data hash
  decodedData: unknown[]; // The attestation data decoded to array.
  id: Hash; // The unique identifier of the attestation.
  recipient: Hash; // The Ethereum address of the recipient of the attestation.
  schemaId: Hash; // The schema identifier associated with the attestation.
  valid: boolean; // A boolean indicating if the attestation is valid, i.e. not expired, not revoked
};

export enum LiquidityAction {
  MINT = 'mint',
  INCREASE = 'increase',
  DECREASE = 'decrease',
}

export type Maybe<T> = T | null | undefined;
